import autoTable from "jspdf-autotable";
import { convertAmountToWords } from "./NumberWordHelper";

const InvoiceDoLayoutPrint = (
    doc,
    pageWidth,
    pageHeight,
    invoiceData,
    REPORT_FOOTER_IMAGE,
    REPORT_HEADER_IMAGE,
    COMPANY_NAME,
    VAT_PERCENTAGE,
    qrCodeImage,
    reportSealImage,
) => {
    const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
        ? JSON.parse(localStorage.getItem("currentUser"))
        : "";
    const VATPERCENTAGE = CURRENT_USER_OBJECT[0]?.vatp;

    if (!invoiceData || invoiceData.length === 0) return;

    doc.setFont("helvetica", "normal");

    const firstItem = invoiceData[0];
    const isVatApplied = firstItem.deliv_site === "VAT Applied";
    const vatPercent = isVatApplied ? Number(VATPERCENTAGE || 0) : 0;
    const vatRate = vatPercent / 100;

    const companyTRN =
        CURRENT_USER_OBJECT[0]?.trn || CURRENT_USER_OBJECT[0]?.TRN || "-";

    const margin = 10;
    const footerHeight = 30;
    const sectionHeight = 25;
    const signatureHeight = 30;
    const bottomBlockHeight = sectionHeight + signatureHeight + 10;

    // ==========================================
    // REUSABLE STATIONERY HEADER RENDERER
    // ==========================================
    const drawHeaderSection = (pdfDoc) => {
        // Space for pre-printed letterhead header
        let currentY = 40;

        // === TAX / COMMERCIAL INVOICE Header ===
        pdfDoc.setFontSize(16);
        pdfDoc.setFont(undefined, "bold");
        pdfDoc.text(
            "DELIVERY ORDER",
            pageWidth / 2,
            currentY,
            { align: "center" },
        );
        currentY += 6;

        pdfDoc.setFontSize(10);
        pdfDoc.setFont(undefined, "normal");
        pdfDoc.text(`TRN NO : ${companyTRN}`, pageWidth / 2, currentY, {
            align: "center",
        });
        currentY += 5;

        pdfDoc.setFontSize(10);

        const boxGap = 2;
        const contentWidth = pageWidth - 2 * margin;
        const leftBoxWidth = contentWidth * 0.65;

        // Build full address & wrap to fit the left box
        const rawAddress = [
            firstItem.address1,
            firstItem.address2,
            firstItem.address3,
        ]
            .filter((addr) => addr && addr.trim() !== "")
            .join(", ");
        const wrappedAddressLines = pdfDoc.splitTextToSize(rawAddress, leftBoxWidth - 4);

        const leftTextLines = [
            firstItem.custref || firstItem.tr_desc || "Client Name",
            ...wrappedAddressLines,
            `TEL: ${firstItem.tel || "-"}`,
            `TRN: ${firstItem.governor || "-"}`,
        ];

        const lineHeightLeft = 5.5;
        const leftBoxHeight = leftTextLines.length * lineHeightLeft + 2;

        const rightTextLines = [
            `Invoice No: ${firstItem.inv_no || "-"}`,
            `Date: ${firstItem.inv_date ? new Date(firstItem.inv_date).toLocaleDateString("en-GB") : "-"}`,
            `LPO No: ${firstItem.lpo_no || "-"}`,
            `DO No: ${firstItem.do_data || "-"}`,
            `Salesman: ${firstItem.sale_man || "-"}`,
        ];
        const lineHeightRight = 5.5;
        const rightBoxHeight = rightTextLines.length * lineHeightRight + 2;
        const boxHeight = Math.max(leftBoxHeight, rightBoxHeight);

        // Draw Left Info
        let leftY = currentY + 4;
        pdfDoc.setFont(undefined, "bold");
        pdfDoc.text(leftTextLines[0], margin + 2, leftY);
        pdfDoc.setFont(undefined, "normal");
        for (let i = 1; i < leftTextLines.length; i++) {
            leftY += lineHeightLeft;
            pdfDoc.text(leftTextLines[i], margin + 2, leftY);
        }

        // Draw Right Info
        const rightBoxX = margin + leftBoxWidth + boxGap;
        let rightY = currentY + 4;
        for (let i = 0; i < rightTextLines.length; i++) {
            pdfDoc.text(rightTextLines[i], rightBoxX + 2, rightY);
            rightY += lineHeightRight;
        }

        // Return next vertical starting point
        return currentY + boxHeight + 5;
    };

    // Run header layout on initial page execution
    const startTableY = drawHeaderSection(doc);

    // === Items Table Headers ===
    const tableHead = [["Code", "Description", "Unit", "Qty"]];

    // Map rows strictly matching the 4 declared columns
    const tableBody = invoiceData.map((item) => {
        const qty = item.tr_qty || 0;
        return [
            item.code || item.tr_code || "",
            item.idesc || item.tr_desc || "",
            item.unit || item.uom || "",
            qty.toString()
        ];
    });

    // Allocate percentages/widths based on your total content layout (e.g. 190mm total printable width for A4 margins)
    const columnStyles = {
        0: { halign: "left", cellWidth: 30 },   // Code
        1: { halign: "left", cellWidth: 110 },  // Description (takes maximum space)
        2: { halign: "center", cellWidth: 25 },  // Unit
        3: { halign: "center", cellWidth: 25 },  // Qty
    };

    autoTable(doc, {
        head: tableHead,
        body: tableBody,
        startY: startTableY,
        theme: "plain",
        tableWidth: "auto",
        margin: {
            top: startTableY, // Safe vertical push point for page 2 onwards
            left: margin,
            right: margin,
            bottom: footerHeight + 5,
        },
        styles: { fontSize: 8, cellPadding: 3, overflow: "linebreak" },
        headStyles: {
            fontStyle: "bold",
            halign: "center",
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            cellPadding: 1,
        },
        columnStyles,

        // Re-runs the document layout logic smoothly when page breaks occur
        willDrawPage: function (data) {
            if (data.pageNumber > 1) {
                drawHeaderSection(doc);
            }
        },
        didDrawCell: function (data) {
            const cell = data.cell;
            doc.setDrawColor(0);
            doc.setLineWidth(0.1);

            doc.line(cell.x, cell.y, cell.x, cell.y + cell.height);
            doc.line(cell.x + cell.width, cell.y, cell.x + cell.width, cell.y + cell.height);

            if (data.row.section === "head") {
                doc.line(cell.x, cell.y, cell.x + cell.width, cell.y);
                doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
            } else if (data.row.index === data.table.body.length - 1) {
                doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
            }
        },
    });

    // ─── After table: check if bottom block fits on current page ──────────────
    const tableEndY = doc.lastAutoTable.finalY || startTableY;
    const spaceLeft = pageHeight - footerHeight - 5 - tableEndY;

    if (spaceLeft < bottomBlockHeight) {
        doc.addPage();
        drawHeaderSection(doc);
    }

    // === Calculations (Retained for backend mapping safely, layout space minimized) ===
    const totalExclVat = invoiceData.reduce(
        (sum, item) => sum + (item.funit_pric || 0) * (item.tr_qty || 0),
        0,
    );
    const specialDiscount = firstItem.disc_amt || firstItem.fdisc_amt || 0;
    const subTotal = totalExclVat - specialDiscount;
    const advancePercent = firstItem.paid_amt || 0;
    const advanceReceived = subTotal * (advancePercent / 100);
    const balanceClaimingAmount = subTotal - advanceReceived;
    const vatAmount = isVatApplied ? (balanceClaimingAmount * vatRate) : 0;
    const grandTotal = balanceClaimingAmount + vatAmount;

    // Minimum display section calculated to avoid overlapping empty panels
    const displaySectionHeight = 15;
    const sectionTopY = pageHeight - footerHeight - signatureHeight - displaySectionHeight - 5;

    // === Signature Section ===
    const sigY = sectionTopY + displaySectionHeight + signatureHeight - 10;

    doc.line(15, sigY, 80, sigY);
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    doc.text("Receiver's Signature", 45, sigY + 5, { align: "center" });

    if (reportSealImage) {
        const sealSize = 20;
        const rightSignatureCenterX = pageWidth - 45;
        const sealX = rightSignatureCenterX - (sealSize / 2);
        const sealY = sigY - sealSize;

        doc.addImage(reportSealImage, "PNG", sealX, sealY, sealSize, sealSize);
    }

    doc.line(pageWidth - 80, sigY, pageWidth - 15, sigY);
    doc.text(`For ${COMPANY_NAME}`, pageWidth - 45, sigY + 5, { align: "center" });

    // === Stamp correct "Page X of Y" on all pages ===
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(`Page ${p} of ${totalPages}`, pageWidth / 2, pageHeight - 10, {
            align: "center",
        });
        doc.setTextColor(0);
    }
};

export default InvoiceDoLayoutPrint;